import React, { useState, useCallback } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { adminAPI } from '../../api/client';
import { Card, Badge, Modal, Alert, Btn, FormInput, Spinner, EmptyState, Avatar, fmt, fmtDate } from '../../components/UI';

export default function AdminWithdrawals() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [resolveItem,  setResolveItem]  = useState(null);
  const [resolveAction, setResolveAction] = useState('approved');
  const [adminNote, setAdminNote]   = useState('');
  const [saving,    setSaving]      = useState(false);
  const [modalError, setModalError] = useState('');
  const [success,    setSuccess]    = useState('');

  const fetchW = useCallback(() => adminAPI.withdrawals(statusFilter), [statusFilter]);
  const { data, loading, error, refetch } = useFetch(fetchW);
  const withdrawals = data?.withdrawals || [];

  function openResolve(w, action) {
    setResolveItem(w);
    setResolveAction(action);
    setAdminNote('');
    setModalError('');
  }

  async function confirmResolve(e) {
    e.preventDefault();
    setSaving(true); setModalError('');
    try {
      await adminAPI.resolveWithdrawal(resolveItem.id, { status: resolveAction, admin_note: adminNote });
      setSuccess(`Withdrawal ${resolveAction} for ${resolveItem.user_name}.`);
      setResolveItem(null);
      refetch();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update withdrawal.');
    } finally { setSaving(false); }
  }

  const FILTERS = [
    { value: 'pending',  label: 'Pending',  color: '#d97706' },
    { value: 'approved', label: 'Approved', color: '#16a34a' },
    { value: 'rejected', label: 'Rejected', color: '#dc2626' },
    { value: '',         label: 'All',      color: '#636b89' },
  ];

  const METHOD_ICONS = { bank_transfer: '🏦', crypto_usdt: '₿', paypal: '🅿' };

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Withdrawals</h1>
          <p style={styles.sub}>Review, approve or reject user withdrawal requests.</p>
        </div>
      </div>

      {success && <Alert type="success" message={success} />}

      {/* Filter tabs */}
      <div style={styles.filters}>
        {FILTERS.map(f => (
          <button key={f.value} onClick={() => setStatusFilter(f.value)}
            style={{ ...styles.filterBtn, ...(statusFilter === f.value ? { background: f.color + '18', borderColor: f.color, color: f.color, fontWeight: 700 } : {}) }}>
            {f.label}
          </button>
        ))}
      </div>

      <Card>
        {loading ? <Spinner /> : error ? <p style={{ color: '#dc2626' }}>{error}</p>
          : withdrawals.length === 0 ? <EmptyState icon="🏦" message={`No ${statusFilter || ''} withdrawals found.`} />
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['User','Country','Amount','Method','Payment Details','Status','Date','Actions'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map(w => (
                    <tr key={w.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Avatar name={w.user_name} size={28} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f5e' }}>{w.user_name}</div>
                            <div style={{ fontSize: 11, color: '#9099b4' }}>{w.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}><span style={{ fontSize: 13, color: '#636b89' }}>{w.user_country || '—'}</span></td>
                      <td style={styles.td}><strong style={{ fontFamily: "'Sora',sans-serif", color: '#1a1f5e', fontSize: 15 }}>{fmt(w.amount)}</strong></td>
                      <td style={styles.td}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                          <span>{METHOD_ICONS[w.payment_method] || '💳'}</span>
                          <span style={{ color: '#636b89', textTransform: 'capitalize' }}>{w.payment_method?.replace('_', ' ')}</span>
                        </span>
                      </td>
                      <td style={styles.td}>
                        {w.payment_details && (
                          <div style={{ fontSize: 12, color: '#9099b4', maxWidth: 160 }}>
                            {w.payment_details.bank_name     && <div>🏦 {w.payment_details.bank_name}</div>}
                            {w.payment_details.account_number && <div>Acct: ••••{String(w.payment_details.account_number).slice(-4)}</div>}
                            {w.payment_details.wallet_address && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>₿ {w.payment_details.wallet_address}</div>}
                            {w.payment_details.paypal_email   && <div>🅿 {w.payment_details.paypal_email}</div>}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}><Badge status={w.status} /></td>
                      <td style={styles.td}><span style={{ fontSize: 12, color: '#9099b4' }}>{fmtDate(w.created_at)}</span></td>
                      <td style={styles.td}>
                        {w.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <Btn variant="success" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => openResolve(w, 'approved')}>✓ Approve</Btn>
                            <Btn variant="danger"  style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => openResolve(w, 'rejected')}>✗ Reject</Btn>
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: '#9099b4' }}>
                            {w.resolved_at ? `Resolved ${fmtDate(w.resolved_at)}` : '—'}
                            {w.admin_note && <div style={{ color: '#d97706', marginTop: 2 }}>{w.admin_note}</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Card>

      {/* Resolve Modal */}
      <Modal
        open={!!resolveItem}
        onClose={() => setResolveItem(null)}
        title={resolveAction === 'approved' ? '✅ Approve Withdrawal' : '❌ Reject Withdrawal'}
      >
        <Alert type="error" message={modalError} />
        {resolveItem && (
          <div style={{ background: resolveAction === 'approved' ? '#dcfce7' : '#fee2e2', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Sora',sans-serif", color: resolveAction === 'approved' ? '#15803d' : '#b91c1c' }}>{fmt(resolveItem.amount)}</div>
            <div style={{ fontSize: 13, color: '#636b89', marginTop: 4 }}>{resolveItem.user_name} · {resolveItem.payment_method?.replace('_', ' ')}</div>
          </div>
        )}
        <form onSubmit={confirmResolve}>
          <FormInput
            label={`Admin Note (${resolveAction === 'rejected' ? 'required — reason for rejection' : 'optional'})`}
            placeholder={resolveAction === 'rejected' ? 'Reason for rejection…' : 'Optional message to the user…'}
            value={adminNote}
            onChange={e => setAdminNote(e.target.value)}
            required={resolveAction === 'rejected'}
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="outline" type="button" onClick={() => setResolveItem(null)}>Cancel</Btn>
            <Btn
              loading={saving} type="submit"
              variant={resolveAction === 'approved' ? 'success' : 'danger'}
            >
              Confirm {resolveAction === 'approved' ? 'Approval' : 'Rejection'}
            </Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

const styles = {
  wrap:       { padding: '32px', fontFamily: "'DM Sans',sans-serif" },
  header:     { marginBottom: 20 },
  h1:         { fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', margin: 0 },
  sub:        { fontSize: 14, color: '#9099b4', marginTop: 4 },
  filters:    { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  filterBtn:  { padding: '7px 18px', borderRadius: 20, border: '1.5px solid #e2e6f0', background: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#636b89', transition: 'all .15s' },
  table:      { width: '100%', borderCollapse: 'collapse', minWidth: 900 },
  th:         { background: '#f8f9fc', padding: '11px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#9099b4', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid #e2e6f0' },
  tr:         { borderBottom: '1px solid #f0f2f8' },
  td:         { padding: '13px 14px', fontSize: 14, verticalAlign: 'middle' },
};
